import React from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const ClientSelectionPanel = ({ clients, selectedClients, onSelectionChange, loading }) => {
  const handleToggleClient = (clientId) => {
    if (selectedClients?.includes(clientId)) {
      onSelectionChange(selectedClients?.filter(id => id !== clientId));
    } else {
      onSelectionChange([...selectedClients, clientId]);
    }
  };

  const handleSelectAll = () => {
    onSelectionChange(clients?.map(c => c?.id));
  };

  const handleDeselectAll = () => {
    onSelectionChange([]);
  };

  if (loading) {
    return (
      <div className="bg-card border border-border rounded-lg p-6">
        <div className="animate-pulse space-y-4">
          <div className="h-6 bg-muted rounded w-3/4" />
          <div className="h-20 bg-muted rounded" />
          <div className="h-20 bg-muted rounded" />
        </div>
      </div>
    );
  }

  return (
    <div className="bg-card border border-border rounded-lg overflow-hidden">
      {/* Header */}
      <div className="p-4 border-b border-border bg-muted/30">
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-semibold text-foreground flex items-center gap-2">
            <Icon name="Building2" size={20} />
            Client Selection
          </h3>
          <span className="text-xs text-muted-foreground">
            {selectedClients?.length} / {clients?.length} selected
          </span>
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={handleSelectAll}
            disabled={selectedClients?.length === clients?.length}
          >
            Select All
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={handleDeselectAll}
            disabled={selectedClients?.length === 0}
          >
            Clear
          </Button>
        </div>
      </div>

      {/* Client List */}
      <div className="p-4 space-y-3 max-h-[600px] overflow-y-auto">
        {clients?.length === 0 ? (
          <div className="text-center py-8 text-muted-foreground">
            <Icon name="Building2" size={48} className="mx-auto mb-3 opacity-30" />
            <p className="text-sm">No clients available</p>
          </div>
        ) : (
          clients?.map(client => {
            const isSelected = selectedClients?.includes(client?.id);
            return (
              <div
                key={client?.id}
                onClick={() => handleToggleClient(client?.id)}
                className={`p-4 rounded-lg border-2 cursor-pointer transition-all ${
                  isSelected
                    ? 'border-primary bg-primary/5' :'border-border hover:border-primary/50 hover:bg-muted/30'
                }`}
              >
                <div className="flex items-start justify-between mb-2">
                  <div className="flex-1">
                    <h4 className="font-medium text-foreground mb-1">
                      {client?.name}
                    </h4>
                    <div className="flex items-center gap-4 text-xs text-muted-foreground">
                      <span className="flex items-center gap-1">
                        <Icon name="Users" size={14} />
                        {client?.employeeCount} employees
                      </span>
                      <span className={`flex items-center gap-1 ${
                        client?.status === 'active' ? 'text-success' : 'text-muted-foreground'
                      }`}>
                        <div className={`w-2 h-2 rounded-full ${
                          client?.status === 'active' ? 'bg-success' : 'bg-muted-foreground'
                        }`} />
                        {client?.status}
                      </span>
                    </div>
                  </div>
                  <div className={`w-5 h-5 rounded border-2 flex items-center justify-center ${
                    isSelected
                      ? 'border-primary bg-primary' :'border-border'
                  }`}>
                    {isSelected && (
                      <Icon name="Check" size={14} color="white" />
                    )}
                  </div>
                </div>

                {/* Test History Summary */}
                {client?.lastTestDate && (
                  <div className="mt-3 pt-3 border-t border-border">
                    <p className="text-xs text-muted-foreground">
                      Last tested: {new Date(client?.lastTestDate)?.toLocaleDateString()}
                    </p>
                  </div>
                )}
              </div>
            );
          })
        )}
      </div>

      {/* Summary Footer */}
      <div className="p-4 border-t border-border bg-muted/30">
        <div className="text-xs text-muted-foreground">
          <p className="mb-1">
            <strong className="text-foreground">
              {selectedClients?.reduce((sum, id) => {
                const client = clients?.find(c => c?.id === id);
                return sum + (client?.employeeCount || 0);
              }, 0)}
            </strong> total employees in selected clients
          </p>
          <p>
            Notifications will be sent to all selected organizations
          </p>
        </div>
      </div>
    </div>
  );
};

export default ClientSelectionPanel;